import type { Difficulty, Pose, PoseJoint } from '../../../games/shared/contract.ts';

/**
 * Кадр телефона для IQ: `{type: '…IqData', gameState, topicId, difficulty, detectionProgress?, pose?}`.
 * Телефон шлёт выбор из пикера (`topicId`, `difficulty`) в каждом кадре: страница на ТВ собирается под него до
 * старта и заново после смены. `topicId: null` — встроенные офлайн-вопросы; нет поля или оно не неотрицательное
 * целое — выбор не менять (кривой кадр не должен пересобирать страницу посреди раунда). Поза — контрактный
 * `Pose` (суставы вида 16:9, зеркально, `c` — уверенность): отсутствующий сустав не виден; без единого
 * сустава `pose` — `null`.
 */
export type IqGameState = 'PREPARING' | 'STARTED' | 'PAUSED';

export const IQ_JOINTS: readonly PoseJoint[] = [
  'nose', 'leftShoulder', 'rightShoulder', 'leftWrist', 'rightWrist',
  'leftIndex', 'rightIndex', 'leftHip', 'rightHip',
];

export interface IqFrame {
  gameState: IqGameState;
  /** `null` — офлайн-вопросы, `undefined` — в кадре нет годного `topicId`: тему не менять. */
  topicId: number | null | undefined;
  /** `null` — в кадре нет `difficulty` (или он неизвестен): сложность не менять. */
  difficulty: Difficulty | null;
  /** Заполнение рамки калибровки на телефоне, 0…1; `null` — поля нет. */
  detectionProgress: number | null;
  pose: Pose | null;
}

const STATES: readonly unknown[] = ['PREPARING', 'STARTED', 'PAUSED'];
const DIFFICULTIES: readonly unknown[] = ['EASY', 'MEDIUM', 'HARD'];

function parsePoint(value: unknown): { x: number; y: number; c?: number } | null {
  if (!value || typeof value !== 'object') return null;
  const { x, y, c } = value as Record<string, unknown>;
  if (typeof x !== 'number' || typeof y !== 'number' || !Number.isFinite(x) || !Number.isFinite(y)) return null;
  return typeof c === 'number' && Number.isFinite(c) ? { x, y, c } : { x, y };
}

function parsePose(value: unknown): Pose | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  const pose: Pose = {};
  IQ_JOINTS.forEach((joint) => {
    const point = parsePoint(raw[joint]);
    if (point) pose[joint] = point;
  });
  return Object.keys(pose).length > 0 ? pose : null;
}

function parseTopicId(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : undefined;
}

export function parseIqFrame(raw: unknown): IqFrame | null {
  if (!raw || typeof raw !== 'object') return null;
  const {
    gameState, topicId, difficulty, detectionProgress, pose,
  } = raw as Record<string, unknown>;
  if (!STATES.includes(gameState)) return null;
  return {
    gameState: gameState as IqGameState,
    topicId: topicId === null ? null : parseTopicId(topicId),
    difficulty: DIFFICULTIES.includes(difficulty) ? (difficulty as Difficulty) : null,
    detectionProgress:
      typeof detectionProgress === 'number' && Number.isFinite(detectionProgress)
        ? Math.min(1, Math.max(0, detectionProgress))
        : null,
    pose: parsePose(pose),
  };
}
